<div class="help-page">

  <!-- Page header -->
  <app-page-header
    title="Help &amp; Support"
    [subtitle]="isStationStaff
      ? 'Your tickets to RAD admin. Advertiser tickets for your station are in Support Inbox.'
      : 'View your tickets and create a new support request'"
    [showBackButton]="true"
    backRoute="/userhome"
    backLabel="Back to Home">
    <button class="btn-primary help-new-btn"
      type="button"
      (click)="openForm()"
      *ngIf="canCreateTicket && !showForm">
      <mat-icon>add</mat-icon>
      Create New Ticket
    </button>
  </app-page-header>

  <!-- Staff routing note -->
  <div class="help-routing-note" *ngIf="isStationStaff">
    <mat-icon>admin_panel_settings</mat-icon>
    <div>
      <strong>Your requests go to the RAD admin team.</strong>
      <p>
        Raise platform, billing/payment, account or general
        issues here. Use Create New Ticket to raise one.
        Your own tickets are listed on this page.
        Support Inbox is only for tickets advertisers send
        to your station.
      </p>
    </div>
  </div>

  <div class="help-success" *ngIf="successMessage && !showForm">
    <div class="help-success__icon">
      <mat-icon>check</mat-icon>
    </div>
    <div class="help-success__body">
      <strong>Ticket Created!</strong>
      <p>{{ successMessage }}</p>
    </div>
  </div>

  <!-- Create ticket form -->
  <div class="help-form-card" *ngIf="showForm">
    <div class="help-form-card__header">
      <h2>{{ isStationStaff ? 'Raise a Ticket with RAD Admin' : 'Submit a Support Request' }}</h2>
      <button class="btn-icon"
        type="button"
        (click)="closeForm()"
        aria-label="Close form">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Success message -->
    <div class="help-success" *ngIf="successMessage">
      <div class="help-success__icon">
        <mat-icon>check</mat-icon>
      </div>
      <div class="help-success__body">
        <strong>Ticket Created!</strong>
        <p>{{ successMessage }}</p>
        <div class="help-success__actions">
          <button class="btn-secondary btn-sm"
            type="button"
            (click)="resetForm()">
            Submit another request
          </button>
        </div>
      </div>
    </div>

    <form *ngIf="!successMessage"
      (ngSubmit)="submitTicket()">

      <!-- Category -->
      <div class="form-group">
        <mat-form-field appearance="outline" class="support-select" subscriptSizing="dynamic">
          <mat-label>Issue Category *</mat-label>
          <mat-select
            [(ngModel)]="selectedCategory"
            name="category"
            required
            panelClass="owner-station-filter-panel">
            <mat-option value="">Select a category...</mat-option>
            <mat-option *ngFor="let cat of categories" [value]="cat.value">
              {{ cat.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <p class="form-hint"
          *ngIf="selectedCategoryObj">
          {{ selectedCategoryObj.description }}
        </p>
        <p class="form-hint" *ngIf="categories.length === 0">
          Categories are loading, or none are available for your role yet.
        </p>
      </div>

      <div class="reference-context-banner"
        *ngIf="referenceId && referenceLabel">
        <div class="reference-context-banner__icon">
          <svg width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="currentColor"
            stroke-width="2">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
            <rect x="9" y="3" width="6" height="4"
              rx="2"/>
          </svg>
        </div>
        <div class="reference-context-banner__text">
          <span class="reference-context-banner__label">
            Related to:
          </span>
          <strong>{{ referenceLabel }}</strong>
        </div>
        <button type="button"
          class="reference-context-banner__clear"
          (click)="referenceType=null; referenceId=null; referenceLabel=''"
          title="Remove reference">
          ×
        </button>
      </div>

      <!-- Subject -->
      <div class="form-group">
        <label class="form-label">Subject *</label>
        <input type="text"
          class="form-input"
          [(ngModel)]="subject"
          name="subject"
          placeholder="Brief description of your issue"
          maxlength="255"
          required>
      </div>

      <!-- Description -->
      <div class="form-group">
        <label class="form-label">
          Describe your issue *
        </label>
        <textarea
          class="form-input form-textarea"
          [(ngModel)]="description"
          name="description"
          rows="5"
          placeholder="Please provide as much detail as possible about your issue..."
          required
          minlength="10">
        </textarea>
      </div>

      <!-- File attachment section -->
      <div class="attachment-section">
        <label class="attachment-label">
          Attachments
          <span class="attachment-hint">
            (optional — max 5 files, 10MB each)
          </span>
        </label>

        <div class="attachment-dropzone"
          (dragover)="$event.preventDefault()"
          (drop)="onAttachmentDrop($event)"
          [class.attachment-dropzone--uploading]="uploadingFiles">

          <input type="file"
            id="attachmentInput"
            multiple
            accept="image/*,.pdf,.txt"
            style="display:none"
            (change)="onAttachmentSelected($event)">

          <label for="attachmentInput"
            class="attachment-dropzone__label">
            <svg width="20" height="20"
              viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
            <span *ngIf="!uploadingFiles">
              Click or drag files to attach
            </span>
            <span *ngIf="uploadingFiles">
              Uploading...
              <span class="btn-spinner-dark"></span>
            </span>
          </label>
        </div>

        <div class="attachment-list"
          *ngIf="pendingAttachments.length > 0">
          <div class="attachment-item"
            *ngFor="let att of pendingAttachments">

            <div class="attachment-item__thumb">
              <img
                *ngIf="isImage(att.file_type)"
                [src]="att.file_url"
                [alt]="att.file_name">
              <svg *ngIf="!isImage(att.file_type)"
                viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>

            <div class="attachment-item__info">
              <span class="attachment-item__name">
                {{ att.file_name }}
              </span>
              <span class="attachment-item__size">
                {{ formatFileSize(att.file_size) }}
              </span>
            </div>

            <button type="button"
              class="attachment-item__remove"
              (click)="removeAttachment(att.id)"
              title="Remove">
              <svg width="14" height="14"
                viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <p class="upload-error" *ngIf="uploadError">
          {{ uploadError }}
        </p>
      </div>

      <!-- Error -->
      <p class="help-form-error" *ngIf="formError">
        {{ formError }}
      </p>

      <!-- Actions -->
      <div class="help-form-actions">
        <button type="button"
          class="btn-secondary"
          (click)="closeForm()">
          Cancel
        </button>
        <button type="submit"
          class="btn-primary"
          [disabled]="isSubmitting">
          <span class="btn-spinner"
            *ngIf="isSubmitting"></span>
          {{ isSubmitting ? 'Submitting...'
            : 'Submit Request' }}
        </button>
      </div>
    </form>
  </div>

  <!-- Tickets the current user raised (advertisers + staff) -->
  <div class="help-tickets">

    <div class="help-tickets__head">
      <div class="help-tickets__title">
        <mat-icon>support_agent</mat-icon>
        <h2>My Tickets</h2>
        <span class="help-count-chip"
          *ngIf="totalTickets > 0">
          {{ totalTickets }}
        </span>
      </div>

      <mat-form-field appearance="outline"
        class="support-select support-select--filter"
        subscriptSizing="dynamic">
        <mat-select
          [(ngModel)]="filterStatus"
          name="filterStatus"
          (selectionChange)="onFilterChange()"
          panelClass="owner-station-filter-panel">
          <mat-option *ngFor="let s of statusOptions" [value]="s.value">
            {{ s.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <!-- Loading -->
    <div class="help-loading" *ngIf="loading">
      <app-data-loader [show]="true"
        message="Loading tickets..."
        size="medium" />
    </div>

    <!-- Empty state -->
    <div class="help-empty"
      *ngIf="!loading && myTickets.length === 0">
      <mat-icon class="help-empty__icon">support_agent</mat-icon>

      <ng-container *ngIf="!filterStatus">
        <p>{{ isStationStaff
          ? 'You have not raised any tickets with RAD admin yet.'
          : 'You haven\'t submitted any support tickets yet.' }}</p>
        <button class="btn-primary"
          type="button"
          (click)="openForm()"
          *ngIf="!showForm">
          Create New Ticket
        </button>
      </ng-container>

      <ng-container *ngIf="filterStatus">
        <p>No tickets match this status.</p>
        <button class="btn-secondary"
          type="button"
          (click)="filterStatus=''; onFilterChange()">
          Show all tickets
        </button>
      </ng-container>
    </div>

    <!-- Tickets table -->
    <div class="help-table-wrap" *ngIf="!loading && myTickets.length > 0">
      <table class="help-table">
        <thead>
          <tr>
            <th>Ticket #</th>
            <th>Subject</th>
            <th>Category</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Created</th>
            <th>Replies</th>
          </tr>
        </thead>
        <tbody>
          <tr class="help-table__row"
            *ngFor="let ticket of myTickets"
            (click)="openTicket(ticket)">
            <td class="ticket-num-cell">
              {{ ticket.ticket_number }}
            </td>
            <td class="ticket-subject-cell">
              {{ ticket.subject }}
            </td>
            <td>
              {{ getCategoryLabel(ticket.category) }}
            </td>
            <td>
              <span [class]="'ticket-status ticket-status--' + ticket.status">
                {{ getStatusLabel(ticket.status) }}
              </span>
            </td>
            <td>
              <span [class]="'ticket-priority ticket-priority--' + ticket.priority">
                {{ ticket.priority }}
              </span>
            </td>
            <td>
              {{ ticket.created_at | date:'MMM d, y' }}
            </td>
            <td>{{ ticket.reply_count || 0 }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="help-pagination"
      *ngIf="!loading && totalTickets > 0">
      <span class="help-pagination__info">
        Showing {{ paginationStart }}–{{ paginationEnd }}
        of {{ totalTickets }}
        {{ totalTickets === 1 ? 'ticket' : 'tickets' }}
      </span>

      <div class="pagination" *ngIf="totalPages > 1">
        <button type="button"
          class="pagination-btn"
          [class.disabled]="currentPage === 1"
          [disabled]="currentPage === 1"
          (click)="goToPage(currentPage - 1)">
          <mat-icon>chevron_left</mat-icon>
          <span class="pagination-btn__label">Previous</span>
        </button>

        <div class="pagination-numbers">
          <button type="button"
            *ngFor="let page of getPaginationPages()"
            class="pagination-number"
            [class.active]="page === currentPage"
            (click)="goToPage(page)">
            {{ page }}
          </button>
        </div>

        <button type="button"
          class="pagination-btn"
          [class.disabled]="currentPage === totalPages"
          [disabled]="currentPage === totalPages"
          (click)="goToPage(currentPage + 1)">
          <span class="pagination-btn__label">Next</span>
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
    </div>
  </div>
</div>
